import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '../../../environment';
import { addZpaxMessageListener, sendZpaxMessage, setTargets } from '../common/utils/myzpax-messaging';
import { isExcludedStateRoute, isFallbackRoute } from '../common/utils/myzpax-routes.util';

const LAST_INTERACTION_MAX_ATTEMPTS = 3;
const LAST_INTERACTION_RETRY_INTERVAL_MS = 1000;

@Injectable({ providedIn: 'root' })
export class MyZpaxService {
  readonly isInMyzpax: boolean;
  private _router = inject(Router);
  private suppressInteraction = false;
  private manualLockHidden = false;

  constructor() {
    try {
      this.isInMyzpax = window.self !== window.top;
    } catch {
      this.isInMyzpax = true;
    }
    if (this.isInMyzpax) {
      setTargets(environment.postMessageTargets);
      this.trackState();
    }
  }

  sendInteraction(): void {
    if (this.suppressInteraction) return;
    sendZpaxMessage('interaction');
  }

  getLastInteraction(): Promise<number> {
    this.suppressInteraction = true;
    return new Promise((resolve) => {
      let attempts = 0;
      let settled = false;
      let retry: ReturnType<typeof setInterval>;
      let unsubscribe: () => void = () => {};

      const settle = (value: number) => {
        if (settled) return;
        settled = true;
        this.suppressInteraction = false;
        clearInterval(retry);
        unsubscribe();
        resolve(value);
      };

      unsubscribe = addZpaxMessageListener('last_interaction', (msg) => settle(msg.data));

      attempts = 1;
      sendZpaxMessage('last_interaction');

      retry = setInterval(() => {
        if (attempts >= LAST_INTERACTION_MAX_ATTEMPTS) {
          settle(0);
          return;
        }
        attempts++;
        sendZpaxMessage('last_interaction');
      }, LAST_INTERACTION_RETRY_INTERVAL_MS);
    });
  }

  onManualLock(handler: () => void): () => void {
    return addZpaxMessageListener('lock_app', () => handler());
  }

  onLastInteraction(handler: (timestamp: number) => void): () => void {
    return addZpaxMessageListener('last_interaction', (msg) => handler(msg.data));
  }

  openContactUs(): void {
    sendZpaxMessage('open_contact_form', {
      appName: 'Rehab Connect',
      toEmail: environment.supportEmail
    })
  }

  setManualLockHidden(hidden: boolean): void {
    if (!this.isInMyzpax) return;
    sendZpaxMessage('hide_manual_lock', hidden);
  }

  private trackState(): void {
    this._router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        const url = window.location.pathname + window.location.search + window.location.hash;
        this.syncManualLockVisibility(url);
        if (!isExcludedStateRoute(url)) {
          sendZpaxMessage('set_state', url);
        }
      });
  }

  private syncManualLockVisibility(url: string): void {
    const shouldHide = isFallbackRoute(url);
    if (shouldHide === this.manualLockHidden) return;
    this.manualLockHidden = shouldHide;
    this.setManualLockHidden(shouldHide);
  }
}
